import { useMutation, useSuspenseQuery } from '@apollo/client';
import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import type { GetCreateQuestionsDataQuery } from '../../apollo/__generated__/graphql.ts';
import { GET_CREATE_QUESTIONS_DATA } from '../../apollo/questions.ts';
import {
  CREATE_QUESTION_SKILL,
  UPDATE_QUESTION_SKILL,
} from '../../apollo/question-skills.ts';
import {
  CREATE_QUESTION_TYPE,
  UPDATE_QUESTION_TYPE,
} from '../../apollo/question-types.ts';
import { useGetMe } from '../../utils/hooks.ts';

type QTypeNode = GetCreateQuestionsDataQuery['question']['types'][number];

type AddScope = {
  key: string;
  sectionId: number;
  domainId?: number;
  skillId?: number;
};

type AddSkillScope = {
  key: string;
  domainId: number;
};

const SECTION_ORDER = ['reading and writing', 'math'];

const DOMAIN_ORDER: Record<string, string[]> = {
  'reading and writing': [
    'craft and structure',
    'information and ideas',
    'standard english conventions',
    'expression of ideas',
  ],
  math: [
    'algebra',
    'advanced math',
    'problem-solving and data analysis',
    'geometry and trigonometry',
  ],
};

const normalizeName = (value?: string | null) =>
  (value ?? '').trim().toLowerCase();

const sortByPreferredOrder = <T extends { name: string }>(
  items: T[],
  preferredOrder: string[],
) => {
  return [...items].sort((a, b) => {
    const aName = normalizeName(a.name);
    const bName = normalizeName(b.name);
    const aIndex = preferredOrder.indexOf(aName);
    const bIndex = preferredOrder.indexOf(bName);

    if (aIndex !== -1 || bIndex !== -1) {
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    }

    return a.name.localeCompare(b.name);
  });
};

const sortByName = <T extends { name: string }>(items: T[]) =>
  [...items].sort((a, b) => a.name.localeCompare(b.name));

const QTypeManager = () => {
  const { isAdmin } = useGetMe();
  const { data, refetch } = useSuspenseQuery(GET_CREATE_QUESTIONS_DATA, {
    errorPolicy: 'all',
  });
  const [createQuestionSkill, { loading: creatingQskill }] = useMutation(
    CREATE_QUESTION_SKILL,
  );
  const [updateQuestionSkill, { loading: updatingQskill }] = useMutation(
    UPDATE_QUESTION_SKILL,
  );
  const [createQuestionType, { loading: creatingQtype }] = useMutation(
    CREATE_QUESTION_TYPE,
  );
  const [updateQuestionType, { loading: updatingQtype }] = useMutation(
    UPDATE_QUESTION_TYPE,
  );

  const [expandedSectionIds, setExpandedSectionIds] = useState<number[]>([]);
  const [expandedDomainIds, setExpandedDomainIds] = useState<number[]>([]);
  const [addingScope, setAddingScope] = useState<AddScope | null>(null);
  const [addingSkillScope, setAddingSkillScope] = useState<AddSkillScope | null>(
    null,
  );
  const [newSkillName, setNewSkillName] = useState('');
  const [newQtypeName, setNewQtypeName] = useState('');
  const [editingSkillId, setEditingSkillId] = useState<number | null>(null);
  const [editingSkillName, setEditingSkillName] = useState('');
  const [editingQtypeId, setEditingQtypeId] = useState<number | null>(null);
  const [editingQtypeName, setEditingQtypeName] = useState('');
  const hasInitializedExpansion = useRef(false);

  const sections = useMemo(() => {
    const satSections = sortByPreferredOrder(
      data?.question?.satSections ?? [],
      SECTION_ORDER,
    );
    const domains = data?.question?.domains ?? [];
    const skills = data?.question?.skills ?? [];
    const types = data?.question?.types ?? [];

    return satSections.map((section) => {
      const sectionDomains = sortByPreferredOrder(
        domains.filter((domain) => domain.sectionId === section.id),
        DOMAIN_ORDER[normalizeName(section.name)] ?? [],
      ).map((domain) => {
        const directTypes = sortByName(
          types.filter(
            (type) => type.domainId === domain.id && !type.skillId,
          ),
        );

        const domainSkills = sortByName(
          skills.filter((skill) => skill.domainId === domain.id),
        ).map((skill) => ({
          ...skill,
          types: sortByName(types.filter((type) => type.skillId === skill.id)),
        }));

        return {
          ...domain,
          directTypes,
          skills: domainSkills,
          totalTypeCount:
            directTypes.length +
            domainSkills.reduce((acc, skill) => acc + skill.types.length, 0),
        };
      });

      const sectionTypes = sortByName(
        types.filter(
          (type) =>
            type.sectionId === section.id && !type.domainId && !type.skillId,
        ),
      );

      return {
        ...section,
        directTypes: sectionTypes,
        domains: sectionDomains,
        skillCount: sectionDomains.reduce(
          (acc, domain) => acc + domain.skills.length,
          0,
        ),
        totalTypeCount:
          sectionTypes.length +
          sectionDomains.reduce((acc, domain) => acc + domain.totalTypeCount, 0),
      };
    });
  }, [data]);

  useEffect(() => {
    const nextSectionIds = sections.map((section) => section.id);
    const nextDomainIds = sections.flatMap((section) =>
      section.domains.map((domain) => domain.id),
    );

    if (!hasInitializedExpansion.current) {
      setExpandedSectionIds(nextSectionIds);
      setExpandedDomainIds(nextDomainIds);
      hasInitializedExpansion.current = true;
      return;
    }

    setExpandedSectionIds((current) => {
      const existingIds = current.filter((id) => nextSectionIds.includes(id));
      const newIds = nextSectionIds.filter((id) => !existingIds.includes(id));

      return [...existingIds, ...newIds];
    });
    setExpandedDomainIds((current) => {
      const existingIds = current.filter((id) => nextDomainIds.includes(id));
      const newIds = nextDomainIds.filter((id) => !existingIds.includes(id));

      return [...existingIds, ...newIds];
    });
  }, [sections]);

  const toggleSection = (sectionId: number) => {
    setExpandedSectionIds((current) =>
      current.includes(sectionId)
        ? current.filter((id) => id !== sectionId)
        : [...current, sectionId],
    );
  };

  const toggleDomain = (domainId: number) => {
    setExpandedDomainIds((current) =>
      current.includes(domainId)
        ? current.filter((id) => id !== domainId)
        : [...current, domainId],
    );
  };

  const expandAll = () => {
    setExpandedSectionIds(sections.map((section) => section.id));
    setExpandedDomainIds(
      sections.flatMap((section) => section.domains.map((domain) => domain.id)),
    );
  };

  const collapseAll = () => {
    setExpandedSectionIds([]);
    setExpandedDomainIds([]);
  };

  const closeAddQtype = () => {
    setAddingScope(null);
    setNewQtypeName('');
  };

  const closeAddSkill = () => {
    setAddingSkillScope(null);
    setNewSkillName('');
  };

  const startRenameSkill = (skillId: number, skillName: string) => {
    setEditingSkillId(skillId);
    setEditingSkillName(skillName);
  };

  const cancelRenameSkill = () => {
    setEditingSkillId(null);
    setEditingSkillName('');
  };

  const startRenameQtype = (qtypeId: number, qtypeName: string) => {
    setEditingQtypeId(qtypeId);
    setEditingQtypeName(qtypeName);
  };

  const cancelRenameQtype = () => {
    setEditingQtypeId(null);
    setEditingQtypeName('');
  };

  const saveNewQtype = async () => {
    if (!addingScope || !newQtypeName.trim()) return;

    try {
      const savePromise = createQuestionType({
        variables: {
          input: {
            name: newQtypeName.trim(),
            sectionId: addingScope.sectionId,
            ...(addingScope.domainId && { domainId: addingScope.domainId }),
            ...(addingScope.skillId && { skillId: addingScope.skillId }),
          },
        },
      });

      toast.promise(savePromise, {
        pending: 'Saving qtype...',
        success: 'Qtype added',
        error: 'Failed to save qtype',
      });

      await savePromise;
      closeAddQtype();
      await refetch();
    } catch {
      // Keep the form open so the admin can adjust the name.
    }
  };

  const saveNewSkill = async () => {
    if (!addingSkillScope || !newSkillName.trim()) return;

    try {
      const savePromise = createQuestionSkill({
        variables: {
          input: {
            name: newSkillName.trim(),
            domainId: addingSkillScope.domainId,
          },
        },
      });

      toast.promise(savePromise, {
        pending: 'Saving qskill...',
        success: 'Qskill added',
        error: 'Failed to save qskill',
      });

      await savePromise;
      closeAddSkill();
      await refetch();
    } catch {
      // Keep the form open so the admin can adjust the name.
    }
  };

  const saveRenameSkill = async (skillId: number) => {
    if (!editingSkillName.trim()) return;

    try {
      const savePromise = updateQuestionSkill({
        variables: {
          input: {
            id: skillId,
            name: editingSkillName.trim(),
          },
        },
      });

      toast.promise(savePromise, {
        pending: 'Renaming qskill...',
        success: 'Qskill renamed',
        error: 'Failed to rename qskill',
      });

      await savePromise;
      cancelRenameSkill();
      await refetch();
    } catch {
      // Keep the form open so the admin can adjust the name.
    }
  };

  const saveRenameQtype = async (qtypeId: number) => {
    if (!editingQtypeName.trim()) return;

    try {
      const savePromise = updateQuestionType({
        variables: {
          input: {
            id: qtypeId,
            name: editingQtypeName.trim(),
          },
        },
      });

      toast.promise(savePromise, {
        pending: 'Renaming qtype...',
        success: 'Qtype renamed',
        error: 'Failed to rename qtype',
      });

      await savePromise;
      cancelRenameQtype();
      await refetch();
    } catch {
      // Keep the form open so the admin can adjust the name.
    }
  };

  const renderAddQtypeForm = (scope: AddScope, placeholder: string) => {
    if (addingScope?.key !== scope.key) {
      return (
        <button
          className="text-xs font-medium text-blue hover:underline"
          onClick={() => {
            setAddingScope(scope);
            setNewQtypeName('');
          }}
          type="button"
        >
          Add Qtype
        </button>
      );
    }

    return (
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
        <input
          autoFocus
          className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          onChange={(e) => setNewQtypeName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void saveNewQtype();
            }

            if (e.key === 'Escape') {
              closeAddQtype();
            }
          }}
          placeholder={placeholder}
          value={newQtypeName}
        />
        <div className="flex items-center gap-2">
          <button
            className="rounded bg-blue px-3 py-2 text-sm font-medium text-white hover:opacity-90"
            disabled={creatingQtype || !newQtypeName.trim()}
            onClick={() => void saveNewQtype()}
            type="button"
          >
            Save Qtype
          </button>
          <button
            className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
            onClick={closeAddQtype}
            type="button"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderAddSkillForm = (scope: AddSkillScope, placeholder: string) => {
    if (addingSkillScope?.key !== scope.key) {
      return (
        <button
          className="text-xs font-medium text-blue hover:underline"
          onClick={() => {
            setAddingSkillScope(scope);
            setNewSkillName('');
          }}
          type="button"
        >
          Add Skill
        </button>
      );
    }

    return (
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
        <input
          autoFocus
          className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          onChange={(e) => setNewSkillName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void saveNewSkill();
            }

            if (e.key === 'Escape') {
              closeAddSkill();
            }
          }}
          placeholder={placeholder}
          value={newSkillName}
        />
        <div className="flex items-center gap-2">
          <button
            className="rounded bg-blue px-3 py-2 text-sm font-medium text-white hover:opacity-90"
            disabled={creatingQskill || !newSkillName.trim()}
            onClick={() => void saveNewSkill()}
            type="button"
          >
            Save Skill
          </button>
          <button
            className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
            onClick={closeAddSkill}
            type="button"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderQtypes = (
    qtypes: QTypeNode[],
    emptyText: string,
  ) => {
    return (
      <div className="space-y-2">
        {qtypes.length === 0 ? (
          emptyText ? <p className="text-sm text-gray-500">{emptyText}</p> : null
        ) : (
          qtypes.map((qtype) => (
            <div
              key={qtype.id}
              className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
            >
              {editingQtypeId === qtype.id ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    autoFocus
                    className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    onChange={(e) => setEditingQtypeName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        void saveRenameQtype(qtype.id);
                      }

                      if (e.key === 'Escape') {
                        cancelRenameQtype();
                      }
                    }}
                    value={editingQtypeName}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      className="rounded bg-blue px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
                      disabled={updatingQtype || !editingQtypeName.trim()}
                      onClick={() => void saveRenameQtype(qtype.id)}
                      type="button"
                    >
                      Save
                    </button>
                    <button
                      className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
                      onClick={cancelRenameQtype}
                      type="button"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <span
                      className="rounded-md border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.14em]"
                      style={{
                        backgroundColor: '#fef3c7',
                        borderColor: '#fcd34d',
                        color: '#92400e',
                      }}
                    >
                      Qtype
                    </span>
                    <span className="text-sm text-gray-700">{qtype.name}</span>
                  </div>
                  {isAdmin && (
                    <button
                      className="text-xs font-medium text-blue hover:underline"
                      onClick={() => startRenameQtype(qtype.id, qtype.name)}
                      type="button"
                    >
                      Rename
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div>
      <section className="flex items-start justify-between gap-4">
        <div>
          <h1>QType Manager</h1>
          <p className="description">
            Manage SAT sections, question domains, qskills, and qtypes.
          </p>
        </div>
        {sections.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              className="rounded border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
              onClick={expandAll}
              type="button"
            >
              Expand all
            </button>
            <button
              className="rounded border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
              onClick={collapseAll}
              type="button"
            >
              Collapse all
            </button>
          </div>
        )}
      </section>

      <div className="mt-6 space-y-4 rounded border border-gray-200 bg-white p-6">
        {sections.map((section) => {
          const isSectionExpanded = expandedSectionIds.includes(section.id);

          return (
            <div
              key={section.id}
              className="overflow-hidden rounded-lg border border-gray-200"
            >
              <div className="flex flex-col gap-3 bg-gray-50 px-4 py-3 md:flex-row md:items-start md:justify-between">
                <button
                  className="flex min-w-0 flex-1 items-start gap-3 rounded-md p-1 text-left transition-colors hover:bg-blue-50"
                  onClick={() => toggleSection(section.id)}
                  type="button"
                >
                  {isSectionExpanded ? (
                    <ChevronDownIcon className="mt-0.5 h-5 w-5 flex-none text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="mt-0.5 h-5 w-5 flex-none text-gray-500" />
                  )}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                      <span
                        className="rounded-md border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.14em]"
                        style={{
                          backgroundColor: '#e0f2fe',
                          borderColor: '#7dd3fc',
                          color: '#075985',
                        }}
                      >
                        Section
                      </span>
                      <div className="font-medium text-gray-900">
                        {section.name}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {section.domains.length} Domains • {section.skillCount}{' '}
                      Qskills • {section.totalTypeCount} Qtypes
                    </div>
                  </div>
                </button>
              </div>

              {isSectionExpanded && (
                <div className="space-y-4 border-t border-gray-100 bg-white px-4 py-4">
                  <div className="space-y-3">
                    {section.domains.map((domain) => {
                      const isDomainExpanded = expandedDomainIds.includes(
                        domain.id,
                      );

                      return (
                        <div
                          key={domain.id}
                          className="overflow-hidden rounded-lg border border-gray-200"
                        >
                          <div className="flex flex-col gap-3 bg-white px-4 py-3 md:flex-row md:items-start md:justify-between">
                            <button
                              className="flex min-w-0 flex-1 items-start gap-3 rounded-md p-1 text-left transition-colors hover:bg-blue-50"
                              onClick={() => toggleDomain(domain.id)}
                              type="button"
                            >
                              {isDomainExpanded ? (
                                <ChevronDownIcon className="mt-0.5 h-5 w-5 flex-none text-gray-500" />
                              ) : (
                                <ChevronRightIcon className="mt-0.5 h-5 w-5 flex-none text-gray-500" />
                              )}
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                  <span
                                    className="rounded-md border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.14em]"
                                    style={{
                                      backgroundColor: '#dcfce7',
                                      borderColor: '#86efac',
                                      color: '#166534',
                                    }}
                                  >
                                    Domain
                                  </span>
                                  <div className="font-medium text-gray-900">
                                    {domain.name}
                                  </div>
                                </div>
                                <div className="text-sm text-gray-500">
                                  {domain.skills.length} Qskills •{' '}
                                  {domain.totalTypeCount} Qtypes
                                </div>
                              </div>
                            </button>

                            {isAdmin && (
                              <div className="md:pl-4">
                                {renderAddSkillForm(
                                  {
                                    key: `domain-${domain.id}`,
                                    domainId: domain.id,
                                  },
                                  `New ${domain.name} skill`,
                                )}
                              </div>
                            )}
                          </div>

                          {isDomainExpanded && (
                            <div className="space-y-4 border-t border-gray-100 bg-gray-50 px-4 py-4">
                              {domain.directTypes.length > 0 && (
                                <div className="rounded-lg border border-gray-100 bg-white px-4 py-4">
                                  <div className="mb-3 flex flex-wrap items-center gap-2">
                                    <span
                                      className="rounded-md border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.14em]"
                                      style={{
                                        backgroundColor: '#fef3c7',
                                        borderColor: '#fcd34d',
                                        color: '#92400e',
                                      }}
                                    >
                                      Domain Qtypes
                                    </span>
                                  </div>
                                  {renderQtypes(
                                    domain.directTypes,
                                    'No domain-level qtypes yet.',
                                  )}
                                </div>
                              )}

                              <div className="space-y-3">
                                {domain.skills.map((skill) => (
                                  <div
                                    key={skill.id}
                                    className="rounded-lg border border-gray-100 bg-white px-4 py-4"
                                  >
                                    {editingSkillId === skill.id ? (
                                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                                          <span
                                            className="rounded-md border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.14em]"
                                            style={{
                                              backgroundColor: '#eef2ff',
                                              borderColor: '#c7d2fe',
                                              color: '#4338ca',
                                            }}
                                          >
                                            Qskill
                                          </span>
                                          <input
                                            autoFocus
                                            className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            onChange={(e) =>
                                              setEditingSkillName(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                void saveRenameSkill(skill.id);
                                              }

                                              if (e.key === 'Escape') {
                                                cancelRenameSkill();
                                              }
                                            }}
                                            value={editingSkillName}
                                          />
                                        </div>

                                        <div className="flex items-center gap-2">
                                          <button
                                            className="rounded bg-blue px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
                                            disabled={
                                              updatingQskill ||
                                              !editingSkillName.trim()
                                            }
                                            onClick={() =>
                                              void saveRenameSkill(skill.id)
                                            }
                                            type="button"
                                          >
                                            Save
                                          </button>
                                          <button
                                            className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
                                            onClick={cancelRenameSkill}
                                            type="button"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                        <div className="min-w-0">
                                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                            <span
                                              className="rounded-md border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.14em]"
                                              style={{
                                                backgroundColor: '#eef2ff',
                                                borderColor: '#c7d2fe',
                                                color: '#4338ca',
                                              }}
                                            >
                                              Qskill
                                            </span>
                                            <div className="font-medium text-gray-900">
                                              {skill.name}
                                            </div>
                                          </div>
                                          <div className="text-sm text-gray-500">
                                            {skill.types.length} Qtype
                                            {skill.types.length === 1
                                              ? ''
                                              : 's'}
                                          </div>
                                        </div>

                                        {isAdmin && (
                                          <div className="flex items-center gap-3 md:pl-4">
                                            <button
                                              className="text-xs font-medium text-blue hover:underline"
                                              onClick={() =>
                                                startRenameSkill(
                                                  skill.id,
                                                  skill.name,
                                                )
                                              }
                                              type="button"
                                            >
                                              Rename
                                            </button>
                                            {renderAddQtypeForm(
                                              {
                                                key: `skill-${skill.id}`,
                                                sectionId: section.id,
                                                domainId: domain.id,
                                                skillId: skill.id,
                                              },
                                              `New ${skill.name} qtype`,
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    <div className="mt-4">
                                      {renderQtypes(skill.types, '')}
                                    </div>
                                  </div>
                                ))}

                                {domain.skills.length === 0 && (
                                  <div className="rounded-lg border border-dashed border-gray-200 bg-white px-4 py-6 text-sm text-gray-500">
                                    No qskills have been added to this domain yet.
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {section.directTypes.length > 0 && (
                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-4">
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <span
                          className="rounded-md border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.14em]"
                          style={{
                            backgroundColor: '#fef3c7',
                            borderColor: '#fcd34d',
                            color: '#92400e',
                          }}
                        >
                          Section Qtypes
                        </span>
                      </div>
                      {renderQtypes(
                        section.directTypes,
                        'No section-level qtypes yet.',
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QTypeManager;
